import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPublicShare } from '../utils/api';
import CreateRiskProfile from './CreateRiskProfile';
import CreateOnboarding from './CreateOnboarding';
import NonIndividualOnboarding from './NonIndividualOnboarding';

const PublicFormPage = () => {
  const { kind, token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null });
  const [onboardingChoice, setOnboardingChoice] = useState(null); // 'individual' | 'non_individual'

  const isValidKind = kind === 'risk-profile' || kind === 'onboarding';

  useEffect(() => {
    if (!isValidKind) {
      setState({ loading: false, error: 'This link is not valid.' });
      return;
    }
    getPublicShare(token)
      .then((r) => {
        if (r.status === 'submitted') {
          navigate(`/view/${kind}/${token}`, { replace: true });
          return;
        }
        setState({ loading: false, error: null });
      })
      .catch((err) => setState({ loading: false, error: err.message || 'Link not found' }));
  }, [kind, token, navigate, isValidKind]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 text-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Link unavailable</h1>
          <p className="text-slate-600">{state.error}</p>
          <p className="text-slate-600 mt-1">Please contact Rubicon Finservices for a new link.</p>
        </div>
      </div>
    );
  }

  const onSubmitted = () => navigate(`/view/${kind}/${token}`, { replace: true });

  const header = (subtitle) => (
    <div className="bg-white border-b py-4 text-center">
      <h1 className="text-xl font-bold text-blue-900">Rubicon Finservices</h1>
      <p className="text-sm text-slate-600">{subtitle}</p>
    </div>
  );

  if (kind === 'risk-profile') {
    return (
      <div>
        {header('Risk Profiling Questionnaire')}
        <CreateRiskProfile publicToken={token} onSubmitted={onSubmitted} />
      </div>
    );
  }

  // Onboarding — let the client pick Individual vs Non-Individual first
  if (!onboardingChoice) {
    return (
      <div className="min-h-screen bg-slate-50">
        {header('Investor Onboarding Form')}
        <div className="max-w-xl mx-auto px-4 py-12">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 text-center">Who is being onboarded?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <button
              onClick={() => setOnboardingChoice('individual')}
              className="p-6 bg-white border-2 border-slate-200 rounded-xl hover:border-green-500 hover:shadow-md transition text-left"
              data-testid="choose-individual"
            >
              <div className="text-lg font-bold text-slate-900">Individual</div>
              <p className="text-sm text-slate-600 mt-1">A single person / joint holder KYC.</p>
            </button>
            <button
              onClick={() => setOnboardingChoice('non_individual')}
              className="p-6 bg-white border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:shadow-md transition text-left"
              data-testid="choose-non-individual"
            >
              <div className="text-lg font-bold text-slate-900">Non-Individual</div>
              <p className="text-sm text-slate-600 mt-1">Company, Trust, LLP, Partnership, HUF, etc.</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {header(onboardingChoice === 'non_individual' ? 'Non-Individual Onboarding Form' : 'Investor Onboarding Form')}
      {onboardingChoice === 'non_individual'
        ? <NonIndividualOnboarding publicToken={token} onSubmitted={onSubmitted} />
        : <CreateOnboarding publicToken={token} onSubmitted={onSubmitted} />}    </div>
  );
};

export default PublicFormPage;
